// src/utils/exportChoiceModal.js
import React from "react";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 16,
};

const cardStyle = {
  width: "min(520px, 96vw)",
  background: "white",
  borderRadius: 16,
  border: "1px solid #e5e7eb",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const headerStyle = {
  padding: 16,
  borderBottom: "1px solid #eee",
};

const titleStyle = { fontWeight: 900, fontSize: 15 };
const subStyle = { marginTop: 6, fontSize: 13, opacity: 0.75, lineHeight: 1.4 };

const bodyStyle = { padding: 16, display: "grid", gap: 10 };

const btnRowStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const btnBase = {
  padding: "12px 12px",
  borderRadius: 14,
  border: "1px solid #e5e7eb",
  background: "white",
  cursor: "pointer",
  textAlign: "left",
};

const btnPrimary = {
  ...btnBase,
  border: "1px solid #111827",
  background: "#111827",
  color: "white",
};

const btnSecondary = {
  ...btnBase,
  background: "#f8fafc",
};

const btnGhost = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "white",
  cursor: "pointer",
  fontWeight: 900,
};

function ButtonCard({ variant, title, hint, onClick }) {
  const style = variant === "primary" ? btnPrimary : btnSecondary;
  return (
    <button style={style} onClick={onClick}>
      <div style={{ fontWeight: 900, fontSize: 14 }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: 12, opacity: variant === "primary" ? 0.85 : 0.75, lineHeight: 1.35 }}>
        {hint}
      </div>
    </button>
  );
}

function ExportChoiceModal({ open, title, subtitle, options = [], onClose }) {
  if (!open) return null;

  return (
    <div
      style={overlayStyle}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div style={cardStyle}>
        <div style={headerStyle}>
          <div style={titleStyle}>{title || "Choose export format"}</div>
          {subtitle ? <div style={subStyle}>{subtitle}</div> : null}
        </div>

        <div style={bodyStyle}>
          <div style={btnRowStyle}>
            {options.map((opt) => (
              <ButtonCard
                key={opt.key}
                variant={opt.primary ? "primary" : "secondary"}
                title={opt.title}
                hint={opt.hint}
                onClick={opt.onChoose}
              />
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button style={btnGhost} onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Programmatic helper:
 * showExportChoice({ title, subtitle, options }) -> Promise<"KEY" | null>
 *
 * options: [{ key, title, hint, primary? }]
 */
export function showExportChoice({ title, subtitle, options }) {
  return new Promise((resolve) => {
    // Create a mount node
    const mount = document.createElement("div");
    document.body.appendChild(mount);

    const cleanup = () => {
      try {
        root.unmount();
      } catch {}
      mount.remove();
    };

    const handleClose = () => {
      cleanup();
      resolve(null);
    };

    const handleChoose = (key) => {
      cleanup();
      resolve(key);
    };

    // Lazy-load ReactDOM to avoid import in non-browser contexts
    // (CRA supports this fine)
    // eslint-disable-next-line global-require
    const ReactDOM = require("react-dom/client");
    const root = ReactDOM.createRoot(mount);

    root.render(
      <ExportChoiceModal
        open
        title={title}
        subtitle={subtitle}
        onClose={handleClose}
        options={(options || []).map((o) => ({
          ...o,
          onChoose: () => handleChoose(o.key),
        }))}
      />
    );
  });
}
